import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import CopyButton from "../../../../components/CopyButton/CopyButton";
import { ICalendarControlXl } from "../../../../models/calendar-control";
import {
  formatDate,
  formatDateTime,
  formatDocument,
  listStatusConfig,
  translationPath,
} from "./utils";

interface ICalendarControlDetailsModalProps {
  calendarControl: ICalendarControlXl;
  onClose: () => void;
}

const Field = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) => (
  <Grid item xs={12} sm={6} md={4}>
    {/* component="div" em ambos: caption renderiza como <span> e ficaria na mesma linha do valor. */}
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" component="div" sx={{ wordBreak: "break-word" }}>
      {value === null || value === undefined || value === "" ? "-" : value}
    </Typography>
  </Grid>
);

const CalendarControlDetailsModal: React.FC<ICalendarControlDetailsModalProps> = ({
  calendarControl,
  onClose,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const statusCfg = calendarControl.status
    ? listStatusConfig[calendarControl.status]
    : undefined;

  // Copia o registro inteiro (todas as colunas do cabeçalho) como JSON, sem o JSON_CONTENT,
  // que tem modal e botão de copiar próprios.
  const recordJson = useMemo(() => {
    const { jsonContent: _jsonContent, ...rest } = calendarControl;
    return JSON.stringify(rest, null, 2);
  }, [calendarControl]);

  const label = (key: string, fallback: string) => t(translationPath + key) || fallback;

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <span>
            {label("details_modal.title", "Detalhes da busca")} -{" "}
            {calendarControl.identifier}
          </span>
          <CopyButton
            value={recordJson}
            label={label("copy_details", "Copiar detalhes (JSON)")}
          />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={2}>
          <Grid container spacing={2}>
            <Field label={label("identifier", "Identificador")} value={calendarControl.identifier} />
            <Field label={label("created", "Data de criação")} value={formatDateTime(calendarControl.created, language)} />
            <Field label={label("last_modified", "Última modificação")} value={formatDateTime(calendarControl.lastModified, language)} />
            <Field label={label("heart_beat_date", "Último heartbeat")} value={formatDateTime(calendarControl.heartBeatDate, language)} />

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary" component="div">
                {label("status", "Status da lista")}
              </Typography>
              <Stack direction="row">
                {calendarControl.status ? (
                  <Chip
                    label={
                      statusCfg
                        ? t(translationPath + `status_values.${statusCfg.key}`)
                        : calendarControl.status
                    }
                    color={statusCfg?.color ?? "default"}
                    size="small"
                  />
                ) : (
                  "-"
                )}
              </Stack>
            </Grid>
            <Field
              label={label("type_of_appointment", "Tipo de agenda")}
              value={
                calendarControl.typeOfAppointment
                  ? t(translationPath + `type_of_appointment_values.${calendarControl.typeOfAppointment}`) ||
                    calendarControl.typeOfAppointment
                  : undefined
              }
            />
            <Field label={label("type_calendar", "Código do tipo")} value={calendarControl.typeCalendar} />

            <Field label={label("document", "CPF")} value={formatDocument(calendarControl.document)} />
            <Field label={label("birth_date", "Data de nascimento")} value={formatDate(calendarControl.birthDate, language)} />
            <Field label={label("gender", "Sexo")} value={calendarControl.gender} />
            <Field label={label("individual_identifier", "Código da pessoa (Tasy)")} value={calendarControl.individualIdentifier} />

            <Field label={label("insurance_code", "Convênio")} value={calendarControl.insuranceCode} />
            <Field label={label("insurance_category_code", "Categoria do convênio")} value={calendarControl.insuranceCategoryCode} />

            <Field label={label("procedure_code", "Procedimento")} value={calendarControl.procedureCode} />
            <Field label={label("internal_procedure_code", "Procedimento interno")} value={calendarControl.internalProcedureCode} />
            <Field label={label("procedure_origin_code", "Origem do procedimento")} value={calendarControl.procedureOriginCode} />

            <Field label={label("valid_quantity", "Horários válidos")} value={calendarControl.validQuantity} />
            <Field label={label("minimum_quantity", "Quantidade mínima")} value={calendarControl.minimumQuantity} />
          </Grid>

          {calendarControl.error && (
            <Alert severity="error" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {calendarControl.error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{label("close", "Fechar")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalendarControlDetailsModal;
